import { format, formatDistanceToNow } from 'date-fns';
import {
  RetainerFeeType,
  RetainerStatus,
  type RetainerFieldsFragment,
} from '~/gql/graphql';
import { CENTS_PER_DOLLAR } from '~/shared/constants';
import { formatCurrency } from '~/shared/utils/formatCurrency';
import type { RetainerCardReceiptLine } from '~/modules/matters/RetainerCard';
import { hasAnyHiredExpert } from '~/modules/matters/matterExpertUtils';
import type { MatterExpertRow } from '~/modules/matters/matters.types';
import type { RetainerStatusFilterValue } from '~/modules/matters/useMatterDetailModals';

export function getRetainerStatusFilterLabel(
  value: RetainerStatusFilterValue,
): string {
  if (value === 'all') {
    return 'All';
  }
  if (value === 'pending') {
    return 'Pending';
  }
  if (value === 'active') {
    return 'Active';
  }
  return 'Completed';
}

/** Balance slice from `useMatterDetailRetainers` / `RetainersTabContent` props. */
export interface RetainersTabBalanceSnapshot {
  hoursPurchased: number;
  flatAmountPurchased?: number;
}

function noRetainerBalanceFromBalance(
  balance: RetainersTabBalanceSnapshot | null,
): boolean {
  return Boolean(
    balance &&
      balance.hoursPurchased === 0 &&
      (balance.flatAmountPurchased ?? 0) === 0,
  );
}

function hasHoursBalanceFromBalance(
  balance: RetainersTabBalanceSnapshot | null,
): boolean {
  return Boolean(balance && balance.hoursPurchased > 0);
}

export interface HasRetainersPaperContentParams {
  isAttorney: boolean;
  isMatterClosed: boolean;
  matterExperts: readonly MatterExpertRow[] | null | undefined;
  balance: RetainersTabBalanceSnapshot | null;
  pendingRequestsLength: number;
  canExpertRequestRetainer: boolean;
  expertStripeConnected: boolean;
}

/** Same predicate as the main-column `Paper` wrapper in `RetainersTabContent`. */
export function getHasRetainersPaperContent(
  params: HasRetainersPaperContentParams,
): boolean {
  if (params.isMatterClosed) {
    return false;
  }

  const noRetainerBalance = noRetainerBalanceFromBalance(params.balance);
  const hasHoursBalance = hasHoursBalanceFromBalance(params.balance);
  return (
    (params.isAttorney &&
      hasAnyHiredExpert(params.matterExperts) &&
      !hasHoursBalance &&
      !(noRetainerBalance && params.pendingRequestsLength === 0)) ||
    (params.isAttorney && params.pendingRequestsLength > 0) ||
    (noRetainerBalance && params.pendingRequestsLength === 0) ||
    (noRetainerBalance &&
      params.pendingRequestsLength > 0 &&
      params.canExpertRequestRetainer &&
      params.expertStripeConnected)
  );
}

/** Inputs for the visible retainer count — balance flags derived inside the counter. */
export interface VisibleRetainerMainListCardsParams {
  isMatterClosed: boolean;
  isAttorney: boolean;
  matterExperts: readonly MatterExpertRow[] | null | undefined;
  balance: RetainersTabBalanceSnapshot | null;
  filteredPendingRequests: readonly RetainerFieldsFragment[];
  retainerStatusFilter: RetainerStatusFilterValue;
  filteredFlatRateRetainers: readonly RetainerFieldsFragment[];
  filteredActiveRetainers: readonly RetainerFieldsFragment[];
  canExpertRequestRetainer: boolean;
  expertStripeConnected: boolean;
}

/**
 * Count of visible retainer-related blocks in the main column (excludes Retainer History).
 * Derives balance and paper visibility from `balance` + matter experts (no separate booleans).
 */
export function countVisibleRetainerMainListCards(
  params: VisibleRetainerMainListCardsParams,
): number {
  if (params.isMatterClosed) {
    return 0;
  }

  const noRetainerBalance = noRetainerBalanceFromBalance(params.balance);
  const hasHoursBalance = hasHoursBalanceFromBalance(params.balance);

  const hasRetainersPaperContent = getHasRetainersPaperContent({
    isAttorney: params.isAttorney,
    isMatterClosed: params.isMatterClosed,
    matterExperts: params.matterExperts,
    balance: params.balance,
    pendingRequestsLength: params.filteredPendingRequests.length,
    canExpertRequestRetainer: params.canExpertRequestRetainer,
    expertStripeConnected: params.expertStripeConnected,
  });

  const showPendingBranch =
    params.isAttorney &&
    params.filteredPendingRequests.length > 0 &&
    noRetainerBalance &&
    (params.retainerStatusFilter === 'all' ||
      params.retainerStatusFilter === 'pending');

  if (showPendingBranch) {
    return params.filteredPendingRequests.length;
  }

  let count = 0;

  const hasPendingCardsInList =
    !params.isAttorney &&
    params.filteredPendingRequests.length > 0 &&
    (params.retainerStatusFilter === 'all' ||
      params.retainerStatusFilter === 'pending');

  const showFundedRetainersSection =
    (hasHoursBalance ||
      params.filteredActiveRetainers.length > 0 ||
      params.filteredFlatRateRetainers.length > 0) &&
    (params.retainerStatusFilter === 'all' ||
      params.retainerStatusFilter === 'active' ||
      params.retainerStatusFilter === 'completed');

  if (hasPendingCardsInList) {
    count += params.filteredPendingRequests.length;
  }

  if (
    showFundedRetainersSection &&
    (params.filteredActiveRetainers.length > 0 ||
      params.filteredFlatRateRetainers.length > 0)
  ) {
    count +=
      params.filteredActiveRetainers.length +
      params.filteredFlatRateRetainers.length;
  }

  if (
    hasRetainersPaperContent &&
    params.isAttorney &&
    params.filteredPendingRequests.length > 0
  ) {
    count += params.filteredPendingRequests.length;
  }

  return count;
}

export const cardActionButtonSx = {
  flex: '1 1 0',
  minWidth: 0,
  textTransform: 'none' as const,
};

/** Card heading: always the retainer title. */
export function retainerCardTitle(retainer: RetainerFieldsFragment): string {
  const trimmed = retainer.title?.trim();
  if (trimmed) {
    return trimmed;
  }
  return 'Retainer request';
}

/** Optional second line on cards when `additionalDetails` is set. */
export function retainerCardSubtitle(
  retainer: RetainerFieldsFragment,
): string | undefined {
  const detail = retainer.additionalDetails?.trim();
  if (detail) {
    return detail;
  }
  return undefined;
}

/** Summary line for modals / payloads: prefer details, else title. */
export function retainerCardDescription(
  retainer: RetainerFieldsFragment,
): string {
  const detail = retainer.additionalDetails?.trim();
  if (detail) {
    return detail;
  }
  return retainer.title;
}

/** Label for history rows (closed vs declined retainers from `retainersForMatter`). */
export function historyRetainerOutcomeLabel(status: RetainerStatus): string {
  switch (status) {
    case RetainerStatus.Declined: {
      return 'Declined';
    }
    case RetainerStatus.Closed: {
      return 'Closed';
    }
    default: {
      return 'Closed';
    }
  }
}

export function formatRetainerRequestAmount(
  retainer: RetainerFieldsFragment,
): string {
  const hourlyFeeDollars =
    retainer.hourlyFee !== null && retainer.hourlyFee !== undefined
      ? retainer.hourlyFee / CENTS_PER_DOLLAR
      : 0;
  const hoursRaw = retainer.hoursRequested;
  const hours =
    hoursRaw !== null && hoursRaw !== undefined && hoursRaw !== ''
      ? Number.parseFloat(hoursRaw)
      : 0;
  const subtotalDollars = retainer.subtotal / CENTS_PER_DOLLAR;
  if (
    retainer.feeType === RetainerFeeType.FlatRate ||
    !Number.isFinite(hours) ||
    hours === 0
  ) {
    return formatCurrency(subtotalDollars);
  }
  return `${hours}h @ ${formatCurrency(hourlyFeeDollars)}/hr = ${formatCurrency(
    subtotalDollars,
  )}`;
}

export function buildReceiptLinesForPendingRetainer(
  retainer: RetainerFieldsFragment,
): RetainerCardReceiptLine[] {
  return [
    {
      label: 'Created',
      value: format(new Date(retainer.createdAt), 'MMM d, yyyy'),
    },
    {
      label: 'Requested',
      value: formatDistanceToNow(new Date(retainer.createdAt), {
        addSuffix: true,
      }),
    },
    { label: 'Total amount', value: formatRetainerRequestAmount(retainer) },
  ];
}

export function buildReceiptLinesForActiveRetainer(
  retainer: RetainerFieldsFragment,
  amountStr: string,
): RetainerCardReceiptLine[] {
  const lines: RetainerCardReceiptLine[] = [
    {
      label: 'Created',
      value: format(new Date(retainer.createdAt), 'MMM d, yyyy'),
    },
  ];
  if (retainer.attorneyAgreedAt) {
    lines.push({
      label: 'Accepted',
      value: format(new Date(retainer.attorneyAgreedAt), 'MMM d, yyyy'),
    });
  }
  lines.push({ label: 'Total amount', value: amountStr });
  return lines;
}
